// Winback — bir BackOffice müşteri ID'si için analiz (Görev #114, 2026-09-21): kayıt tarihi,
// son giriş, son 10 yatırım zamanı; seçili TAKVİM AYI için yatırımların haftanın gününe /
// ayın haftasına / günün saatine kırılımı, kullanılan bonuslar ve en çok oynanan 10 oyun.
//
// Veri CANLI BackOffice okumasıdır (/api/winback; sunucu 2 dk cache). Her kaynak ayrı ayrı
// okunamayabilir: `sources` bunu adıyla söyler ve okunamayan kart sıfır DEĞİL "okunamadı"
// olarak çizilir (§6: yer tutucu yok, ama sessiz sıfır da yok).
//
// Sayfa KİŞİ bazlıdır: nav yalnız `user.features.winback` true olan oturumda görünür; sunucu
// aynı listeyi rotada uygular (403). Marka: yalnız BackOffice'i yapılandırılmış markalar
// (WINBACK_BRANDS) — üstteki marka filtresiyle kesişir, tek marka kaldıysa seçici gizlenir.
//
// Görev #168 (2026-10-04): DÖNEM seçici (Ay | Tüm zamanlar — aynı kartlar tüm geçmiş için),
// son 10 yatırımın yanında son 10 çekim / oyun işlemi / kupon (2×2), ve ayrı araç olarak
// `/winback` adresi (App.tsx WinbackStandalone — aynı sayfa, panel menüsü olmadan).
//
// Sahip 2026-10-07: dönem seçici KALKTI. Sorgu her zaman tüm zamanlar; altında "Aylık kırılım"
// her takvim ayını aynı kartlarla verir (sunucu `monthly`, aynı okumadan — ek BackOffice çağrısı
// yok). Aynı bileşen ayrı Winback sitesinde de çalışır (src/winback-site/, web/src/winback-site/).

import { useEffect, useMemo, useState } from 'react';
import { ApiError, getWinback } from '../api';
import { brandLabel, tzOfBrands, WINBACK_BRANDS } from '../constants';
import { fmtMoney, fmtStamp } from '../format';
import type { BrandId, Lang, WinbackBonus, WinbackCoupon, WinbackDeposit, WinbackDeposits, WinbackGame, WinbackGameTx, WinbackMonth, WinbackReport, WinbackSourceKey, WinbackSourceStatus } from '../types';
import { cardStyle, EmptyState, ghostBtnStyle, Icon, inputStyle, primaryBtnStyle, Select, Skeleton } from '../ui';

const WEEKDAY_TR = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const SOURCE_LABEL: Record<WinbackSourceKey, string> = {
  profile: 'Müşteri bilgisi',
  deposits: 'Ay içi yatırımlar',
  lastDeposits: 'Son yatırımlar',
  lastWithdrawals: 'Son çekimler',
  lastGames: 'Son oyun işlemleri',
  lastCoupons: 'Son kuponlar',
  bonuses: 'Bonuslar',
  freespins: 'Freespinler',
  games: 'Oyunlar',
};

const STATUS_NOTE: Record<Exclude<WinbackSourceStatus, 'ok'>, string> = {
  not_found: 'Müşteri bulunamadı',
  permission: 'BackOffice hesabının bu uç için yetkisi yok',
  unconfigured: 'Bu marka için BackOffice yapılandırılmamış',
  throttled: 'BackOffice istekleri sınırladı — birazdan tekrar deneyin',
  error: 'Okunamadı',
};

const ERROR_NOTE: Record<string, string> = {
  forbidden: 'Bu sayfa için yetkiniz yok.',
  brand_forbidden: 'Bu marka kapsamınızda değil.',
  unconfigured: 'Bu marka için BackOffice yapılandırılmamış.',
  bad_id: 'Müşteri ID sayı olmalı.',
  bad_month: 'Ay seçimi geçersiz.',
  future_month: 'Gelecek bir ay seçilemez.',
  network_error: 'Bağlantı hatası. Lütfen tekrar deneyin.',
};

/** '2026-09' → 'Eylül 2026' / 'September 2026' — dil BURADA seçilir: ay adı + yıl tek metin düğümü
 *  olduğundan EN kaplaması sözlükle çeviremez (sayı taşıyan dizgi kuralı, §2). */
function monthLabel(key: string, lang: Lang): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'tr-TR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1)));
}

const money = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? '' : fmtMoney(n) + ' TL');

/** `small`: tarih+saat gibi uzun değerler (profil kartı) — 22px'te 5 kutu yan yana sığmaz. */
function KpiTile({ label, value, sub, color, small }: { label: string; value: string; sub?: string; color?: string; small?: boolean }) {
  return (
    <div style={{ ...cardStyle, padding: '14px 16px', flex: 1, minWidth: small ? '190px' : '160px' }}>
      <div style={{ fontSize: '10.5px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: small ? '17px' : '22px', fontWeight: 700, color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
      {sub && <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

function CardHead({ icon, title, sub, right }: { icon: string; title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
      <Icon name={icon} size={19} color="var(--accent)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {sub && <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginTop: '1px' }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

/** Adet + tutar satırları: etiket · çubuk (adet oranı) · adet · tutar. Sıfır satır da basılır
 *  (sahip: "her günü görmek istiyorum"); en yüksek adet vurgulanır. */
function BucketTable({ rows, unitLabel }: { rows: { label: string; count: number; amount: number }[]; unitLabel: string }) {
  const max = Math.max(0, ...rows.map((r) => r.count));
  const cols = '140px minmax(0,1fr) 56px 118px';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '10px', padding: '8px 18px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' }}>
        <span>{unitLabel}</span>
        <span />
        <span style={{ textAlign: 'right' }}>ADET</span>
        <span style={{ textAlign: 'right' }}>TUTAR</span>
      </div>
      {rows.map((r) => {
        const top = max > 0 && r.count === max;
        return (
          <div key={r.label} style={{ display: 'grid', gridTemplateColumns: cols, gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <span style={{ fontSize: '12.5px', color: top ? 'var(--text)' : 'var(--dim)', fontWeight: top ? 600 : 500 }}>{r.label}</span>
            <div style={{ height: '10px', background: 'var(--inset)', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: (max ? (r.count / max) * 100 : 0) + '%', height: '100%', background: top ? '#37c46b' : 'var(--accent)', borderRadius: '5px' }} />
            </div>
            <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: r.count ? 'var(--text)' : 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
            <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 600, color: r.count ? 'var(--text)' : 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{r.count ? money(r.amount) : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

function Unavailable({ status, message }: { status: WinbackSourceStatus; message?: string }) {
  const note = status === 'ok' ? '' : STATUS_NOTE[status];
  return (
    <div style={{ padding: '18px', display: 'flex', alignItems: 'center', gap: '10px', color: '#e0a23c', fontSize: '12.5px' }}>
      <Icon name="warning" size={18} color="#e0a23c" />
      <span>{note}</span>
      {message && status === 'error' && <span style={{ color: 'var(--faint)', fontSize: '11.5px' }} title={message}>{'— ' + message.slice(0, 80)}</span>}
    </div>
  );
}

const listHead: React.CSSProperties = { display: 'grid', gap: '10px', padding: '8px 18px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' };
const listRow: React.CSSProperties = { display: 'grid', gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' };
const numCell: React.CSSProperties = { textAlign: 'right', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' };
const ellipsis: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };

function Badge({ text, color }: { text: string; color: string }) {
  return <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.06em', padding: '2px 6px', borderRadius: '6px', background: color + '24', color, flex: 'none' }}>{text}</span>;
}

/** Son 10 kartı kabuğu: başlık (tarama başlangıcı + en yeni kayıt), okunamadı / boş durumu,
 *  satırlar ve (varsa) sayfa tavanı notu. `rows` null = kaynak okunamadı, [] = taranan dönemde yok. */
function LastTenCard({ icon, title, tz, scannedFrom, src, rows, emptyText, note, children }: {
  icon: string;
  title: string;
  tz: string;
  scannedFrom: string | null | undefined;
  src: { status: WinbackSourceStatus; message?: string } | undefined;
  rows: { at: string }[] | null;
  emptyText: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <CardHead
        icon={icon}
        title={title}
        sub={scannedFrom ? 'Tarama başlangıcı ' + fmtStamp(scannedFrom, tz, true) : undefined}
        right={rows && rows.length > 0 ? <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{'Son: ' + fmtStamp(rows[0].at, tz, true)}</span> : null}
      />
      {rows ? (
        <>
          {rows.length === 0 ? <div style={{ padding: '18px', fontSize: '12.5px', color: 'var(--dim)' }}>{emptyText}</div> : children}
          {note && <div style={{ padding: '9px 18px', fontSize: '11.5px', color: '#e0a23c' }}>{note}</div>}
        </>
      ) : (
        <Unavailable status={src?.status || 'error'} message={src?.message} />
      )}
    </div>
  );
}

const payCols = '138px minmax(0,1fr) 112px 52px';

/** Son 10 yatırım / çekim satırları — iki kart aynı şekli paylaşır. */
function PaymentRows({ rows, tz, color }: { rows: WinbackDeposit[]; tz: string; color: string }) {
  return (
    <>
      <div style={{ ...listHead, gridTemplateColumns: payCols }}>
        <span>TARİH</span>
        <span>YÖNTEM</span>
        <span style={{ textAlign: 'right' }}>TUTAR</span>
        <span />
      </div>
      {rows.map((d, i) => (
        <div key={d.id || i} style={{ ...listRow, gridTemplateColumns: payCols }}>
          <span style={{ fontSize: '12.5px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmtStamp(d.at, tz, true)}</span>
          <span style={{ ...ellipsis, fontSize: '12px', color: 'var(--dim)' }} title={d.provider}>{d.provider}</span>
          <span style={{ ...numCell, fontWeight: 700, color }}>{money(d.amount)}</span>
          <span style={{ fontSize: '10.5px', color: 'var(--faint)' }}>{d.manual ? 'Manuel' : ''}</span>
        </div>
      ))}
    </>
  );
}

const gameTxCols = '112px minmax(0,1fr) 62px 96px';

function GameTxRows({ rows, tz }: { rows: WinbackGameTx[]; tz: string }) {
  return (
    <>
      <div style={{ ...listHead, gridTemplateColumns: gameTxCols }}>
        <span>TARİH</span>
        <span>OYUN</span>
        <span>TÜR</span>
        <span style={{ textAlign: 'right' }}>TUTAR</span>
      </div>
      {rows.map((g, i) => (
        <div key={g.id || i} style={{ ...listRow, gridTemplateColumns: gameTxCols }}>
          <span style={{ fontSize: '12px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmtStamp(g.at, tz, true)}</span>
          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <span style={{ ...ellipsis, fontSize: '12.5px', color: g.game ? 'var(--text)' : 'var(--faint)', fontWeight: 500 }} title={g.game || g.label}>{g.game || g.label}</span>
              {g.live && <Badge text="CANLI" color="#e0a23c" />}
              {g.free && <Badge text="FREESPIN" color="#3d8bff" />}
            </span>
            {g.provider && <span style={{ ...ellipsis, fontSize: '11px', color: 'var(--faint)' }} title={g.provider}>{g.provider}</span>}
          </span>
          <span style={{ fontSize: '11.5px', color: g.kind === 'win' ? '#37c46b' : 'var(--dim)' }}>{g.kind === 'bet' ? 'Bahis' : g.kind === 'win' ? 'Kazanç' : g.label}</span>
          <span style={{ ...numCell, fontWeight: 600, color: g.kind === 'win' && (g.amount || 0) > 0 ? '#37c46b' : 'var(--text)' }}>{money(g.amount)}</span>
        </div>
      ))}
    </>
  );
}

const couponCols = '112px minmax(0,1fr) 52px 88px 96px';

const OUTCOME_COLOR: Record<string, string> = { open: '#3d8bff', won: '#37c46b', lost: '#ef5d5d', cashout: '#e0a23c', void: '#9aa4b2' };

/** Durum: sunucu sağlayıcının KENDİ kolonunu BackOffice'in kendi enum'uyla etiketler (engine/winback.js
 *  COUPON_STATUS); bilinmeyen kod numarasıyla basılır — "kazandı/kaybetti" UYDURULMAZ. */
function couponState(c: WinbackCoupon): { text: string; color: string } | null {
  if (c.stateName) return { text: c.stateName, color: OUTCOME_COLOR[c.outcome || ''] || (c.open ? '#3d8bff' : '#9aa4b2') };
  if (c.open) return { text: 'AÇIK', color: '#3d8bff' };
  if (c.state != null) return { text: '#' + c.state, color: 'var(--dim)' };
  return null;
}

function CouponRows({ rows, tz }: { rows: WinbackCoupon[]; tz: string }) {
  return (
    <>
      <div style={{ ...listHead, gridTemplateColumns: couponCols }}>
        <span>TARİH</span>
        <span>KUPON</span>
        <span style={{ textAlign: 'right' }}>KATSAYI</span>
        <span style={{ textAlign: 'right' }}>BAHİS</span>
        <span style={{ textAlign: 'right' }}>OLASI KAZANÇ</span>
      </div>
      {rows.map((c, i) => {
        const st = couponState(c);
        return (
          <div key={c.id || i} style={{ ...listRow, gridTemplateColumns: couponCols }}>
            <span style={{ fontSize: '12px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmtStamp(c.at, tz, true)}</span>
            <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ ...ellipsis, fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }} title={c.ticketId || c.id}>{c.ticketId || c.id}</span>
              <span style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {c.provider && <Badge text={c.provider.toUpperCase()} color="#9aa4b2" />}
                {st && <Badge text={st.text} color={st.color} />}
                {c.freeBet && <Badge text="FREEBET" color="#e0a23c" />}
                {c.bonusFunded && <Badge text="BONUS" color="#e0a23c" />}
              </span>
            </span>
            <span style={{ ...numCell, color: 'var(--text)' }}>{c.coefficient != null ? c.coefficient.toFixed(2) : ''}</span>
            <span style={{ ...numCell, fontWeight: 600, color: 'var(--text)' }}>{money(c.stake)}</span>
            <span style={{ ...numCell, display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
              <span style={{ color: 'var(--dim)' }}>{money(c.possibleWin)}</span>
              {c.win != null && <span style={{ color: '#37c46b', fontWeight: 700 }} title="Ödenen">{money(c.win)}</span>}
            </span>
          </div>
        );
      })}
    </>
  );
}

type SrcOf = (key: WinbackSourceKey) => { status: WinbackSourceStatus; message?: string } | undefined;

/** İki sütunlu kart ızgarası; dar ekranda tek sütun (min(…,100%) yatay taşmayı önler). */
const cardGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(520px,100%),1fr))', gap: '14px', marginBottom: '18px' };

const bonusCols = 'minmax(0,1fr) 96px 48px 100px 60px';
// Sağlayıcı ayrı sütun DEĞİL, oyun adının altında ikinci satır: 6 sabit sütun yarım genişlikte karta sığmıyordu.
const gameCols = '28px minmax(0,1fr) 60px 104px 104px';

/**
 * Bir dönemin gövdesi — KPI'lar, dört yatırım kartı, bonuslar, oyunlar. Tüm zamanlar (`all`) ve
 * aylık kırılımdaki her ay (`month`) AYNI kartları çizer; tek fark takvim kartı (aylar ↔ günler)
 * ve metinler. `null` kaynak = okunamadı (asla 0).
 */
function PeriodBody({ scope, deposits, bonuses, games, gamesDistinct, gamesNote, gamesMissing, noNameKey, src, tz, lang }: {
  scope: 'all' | 'month';
  deposits: WinbackDeposits | null;
  bonuses: WinbackBonus[] | null;
  games: WinbackGame[] | null;
  gamesDistinct: number | null;
  /** Kart altı amber not (sayfa sınırı / kısmi ay). */
  gamesNote?: string;
  /** Oyunlar bu dönem için HİÇ okunmadı (sayfa bütçesi daha yeni aylarda bitti) — hata değil, not. */
  gamesMissing?: string;
  noNameKey: boolean;
  src: SrcOf;
  tz: string;
  lang: Lang;
}) {
  const isAll = scope === 'all';
  const weekdayRows = (deposits?.byWeekday || []).map((r) => ({ label: WEEKDAY_TR[r.weekday - 1], count: r.count, amount: r.amount }));
  const bandRows = (deposits?.byWeekOfMonth || []).map((r) => ({ label: r.key.replace('-', '–') + '. günler', count: r.count, amount: r.amount }));
  const timeRows = (deposits?.byTimeOfDay || []).map((r) => ({ label: r.label, count: r.count, amount: r.amount }));
  const dayRows = (deposits?.byDay || []).map((r) => ({ label: r.day.slice(8, 10) + '.' + r.day.slice(5, 7) + ' ' + WEEKDAY_TR[(new Date(r.day + 'T00:00:00Z').getUTCDay() + 6) % 7], count: r.count, amount: r.amount }));
  // Tüm zamanlarda takvim günü listesi yüzlerce satır olur → yerine takvim ayı (ad + yıl, dil burada seçilir).
  const monthRows = (deposits?.byMonth || []).map((r) => ({ label: monthLabel(r.month, lang), count: r.count, amount: r.amount }));
  const calRows = isAll ? monthRows : dayRows;
  const depUnavailable = <Unavailable status={src('deposits')?.status || 'error'} message={src('deposits')?.message} />;

  return (
    <>
      {deposits && (
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <KpiTile label={isAll ? 'YATIRIM ADEDİ' : 'AY İÇİ YATIRIM ADEDİ'} value={String(deposits.count)} />
          <KpiTile label={isAll ? 'YATIRIM TUTARI' : 'AY İÇİ YATIRIM TUTARI'} value={money(deposits.total)} color="#37c46b" />
          <KpiTile label="YATIRIM YAPILAN GÜN" value={String(deposits.byDay.length)} />
          <KpiTile label="ORTALAMA YATIRIM" value={deposits.count ? money(deposits.total / deposits.count) : ''} />
        </div>
      )}

      <div style={cardGrid}>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <CardHead icon="view_week" title="Haftanın günlerine göre" sub="Hangi gün daha çok yatırım yapıyor" />
          {deposits ? <BucketTable rows={weekdayRows} unitLabel="GÜN" /> : depUnavailable}
        </div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <CardHead icon="date_range" title="Ayın haftalarına göre" sub="1–7 · 8–15 · 16–23 · 24–31" />
          {deposits ? <BucketTable rows={bandRows} unitLabel="AYIN GÜNLERİ" /> : depUnavailable}
        </div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <CardHead icon="schedule" title="Günün saatlerine göre" sub={'Marka saati (' + tz + ')'} />
          {deposits ? <BucketTable rows={timeRows} unitLabel="SAAT ARALIĞI" /> : depUnavailable}
        </div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {isAll ? (
            <CardHead icon="calendar_view_month" title="Aylara göre" sub="Yalnız yatırım olan aylar" />
          ) : (
            <CardHead icon="calendar_view_day" title="Takvim günlerine göre" sub="Yalnız yatırım olan günler" />
          )}
          {deposits ? (
            calRows.length ? (
              <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <BucketTable rows={calRows} unitLabel={isAll ? 'AY' : 'GÜN'} />
              </div>
            ) : (
              <div style={{ padding: '18px', fontSize: '12.5px', color: 'var(--dim)' }}>{isAll ? 'Bu dönemde yatırım yok' : 'Bu ay yatırım yok'}</div>
            )
          ) : (
            depUnavailable
          )}
        </div>
      </div>

      <div style={cardGrid}>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <CardHead icon="redeem" title="Kullanılan bonuslar" sub={isAll ? 'Yatırım bonusları + freespinler, tüm zamanlar' : 'Yatırım bonusları + freespinler, bu ay'} right={bonuses ? <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{bonuses.reduce((n, b) => n + b.count, 0) + ' bonus'}</span> : null} />
          {bonuses ? (
            bonuses.length === 0 ? (
              <div style={{ padding: '18px', fontSize: '12.5px', color: 'var(--dim)' }}>{isAll ? 'Bu dönemde bonus kullanılmamış' : 'Bu ay bonus kullanılmamış'}</div>
            ) : (
              <>
                {(src('bonuses')?.status !== 'ok' || src('freespins')?.status !== 'ok') && (
                  <div style={{ padding: '8px 18px', fontSize: '11.5px', color: '#e0a23c', borderBottom: '1px solid var(--border)' }}>
                    {src('bonuses')?.status !== 'ok' ? 'Yatırım bonusları okunamadı — yalnız freespinler listeleniyor' : 'Freespinler okunamadı — yalnız yatırım bonusları listeleniyor'}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: bonusCols, gap: '10px', padding: '8px 18px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' }}>
                  <span>BONUS</span>
                  <span>TÜR</span>
                  <span style={{ textAlign: 'right' }}>ADET</span>
                  <span style={{ textAlign: 'right' }}>TUTAR / SPİN</span>
                  <span style={{ textAlign: 'right' }}>İPTAL</span>
                </div>
                {bonuses.map((b) => (
                  <div key={b.kind + b.title} style={{ display: 'grid', gridTemplateColumns: bonusCols, gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.title}>{b.title}</span>
                    <span style={{ fontSize: '11.5px', color: 'var(--dim)' }}>{b.kind === 'freespin' ? 'Freespin' : 'Yatırım bonusu'}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{b.count}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{b.kind === 'freespin' ? b.amount + ' spin' : money(b.amount)}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', color: b.cancelled ? '#ef5d5d' : 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{b.cancelled || ''}</span>
                  </div>
                ))}
              </>
            )
          ) : (
            <Unavailable status={src('bonuses')?.status || src('freespins')?.status || 'error'} message={src('bonuses')?.message} />
          )}
        </div>

        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <CardHead
            icon="casino"
            title="En çok oynanan 10 oyun"
            sub={isAll ? 'Bahis turu sayısına göre, tüm zamanlar (freespin turları hariç)' : 'Bahis turu sayısına göre, bu ay (freespin turları hariç)'}
            right={gamesDistinct != null ? <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{gamesDistinct + ' oyun'}</span> : null}
          />
          {games ? (
            games.length === 0 ? (
              <div style={{ padding: '18px', fontSize: '12.5px', color: 'var(--dim)' }}>
                {noNameKey ? 'Oyun adı BackOffice yanıtında yok — sıralama yapılamadı' : isAll ? 'Bu dönemde oyun oynanmamış' : 'Bu ay oyun oynanmamış'}
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: gameCols, gap: '10px', padding: '8px 18px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' }}>
                  <span>#</span>
                  <span>OYUN</span>
                  <span style={{ textAlign: 'right' }}>TUR</span>
                  <span style={{ textAlign: 'right' }}>BAHİS</span>
                  <span style={{ textAlign: 'right' }}>KAZANÇ</span>
                </div>
                {games.map((g, i) => (
                  <div key={g.name} style={{ display: 'grid', gridTemplateColumns: gameCols, gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                    <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                        <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} title={g.name}>{g.name}</span>
                        {g.live && <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.06em', padding: '2px 6px', borderRadius: '6px', background: 'rgba(224,162,60,.14)', color: '#e0a23c', flex: 'none' }}>CANLI</span>}
                      </span>
                      {g.provider && <span style={{ fontSize: '11px', color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={g.provider}>{g.provider}</span>}
                    </span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{g.rounds}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{money(g.stake)}</span>
                    <span style={{ textAlign: 'right', fontSize: '12.5px', color: g.win > g.stake ? '#37c46b' : 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{money(g.win)}</span>
                  </div>
                ))}
              </>
            )
          ) : gamesMissing ? (
            <div style={{ padding: '18px', fontSize: '12.5px', color: '#e0a23c' }}>{gamesMissing}</div>
          ) : (
            <Unavailable status={src('games')?.status || 'error'} message={src('games')?.message} />
          )}
          {games && gamesNote && <div style={{ padding: '9px 18px', fontSize: '11.5px', color: '#e0a23c' }}>{gamesNote}</div>}
        </div>
      </div>
    </>
  );
}

const monthCols = '150px 96px 124px 124px 52px 60px minmax(140px,1fr) 24px';

/** Özet hücresi: okunamayan kaynak '—' (asla 0), sıfır soluk basılır. */
function SumCell({ value, unread, strong, color }: { value: string; unread: boolean; strong?: boolean; color?: string }) {
  return (
    <span style={{ ...numCell, fontWeight: strong ? 700 : 500, color: unread ? 'var(--faint)' : value === '0' || value === '' ? 'var(--faint)' : color || 'var(--text)' }} title={unread ? 'Okunamadı' : undefined}>
      {unread ? '—' : value}
    </span>
  );
}

/**
 * Aylık kırılım (sahip 2026-10-07): tüm zamanların altında HER takvim ayı — ay seçmeye gerek yok.
 * Üstte her ay bir özet satırı (yatırım adet/tutar/ortalama/gün, bonus, en çok oynanan oyun); satıra
 * tıklayınca o ayın tam kartları (PeriodBody 'month') satırın altında açılır. Hepsi kapalı
 * gelir (tüm aylar bir bakışta). Hareketsiz aylar da listelenir (soluk) — "o ay hiçbir şey yapmadı" da bir bilgidir.
 */
function MonthlySection({ months, noNameKey, src, tz, lang }: {
  months: WinbackMonth[];
  noNameKey: boolean;
  src: SrcOf;
  tz: string;
  lang: Lang;
}) {
  // Kapalı başlar: önce HER ayın özet satırı bir bakışta görünsün (açık bir ay altındakileri
  // ekranın çok aşağısına iterdi); detay satıra tıklayınca ya da "Tümünü aç" ile.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const allOpen = months.length > 0 && months.every((m) => open.has(m.key));
  const toggle = (key: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div style={{ ...cardStyle, overflow: 'hidden', marginBottom: '18px' }}>
      <CardHead
        icon="calendar_month"
        title="Aylık kırılım"
        sub="Her ay ayrı — satıra tıklayınca o ayın kartları açılır"
        right={months.length > 0 ? (
          <button type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(months.map((m) => m.key)))} style={{ ...ghostBtnStyle, padding: '6px 11px', fontSize: '12px' }}>
            {allOpen ? 'Tümünü kapat' : 'Tümünü aç'}
          </button>
        ) : null}
      />
      {months.length === 0 ? (
        <div style={{ padding: '18px', fontSize: '12.5px', color: 'var(--dim)' }}>Bu dönemde ay yok</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: '840px' }}>
            <div style={{ ...listHead, gridTemplateColumns: monthCols }}>
              <span>AY</span>
              {/* 'YATIRIM' tek başına sözlüğe giremez (i18n.ts) → tam etiketler */}
              <span style={{ textAlign: 'right' }}>YATIRIM ADEDİ</span>
              <span style={{ textAlign: 'right' }}>YATIRIM TUTARI</span>
              <span style={{ textAlign: 'right' }}>ORTALAMA YATIRIM</span>
              <span style={{ textAlign: 'right' }}>GÜN</span>
              <span style={{ textAlign: 'right' }}>BONUS</span>
              <span>EN ÇOK OYNANAN</span>
              <span />
            </div>
            {months.map((m) => {
              const isOpen = open.has(m.key);
              const dep = m.deposits;
              const bonusCount = m.bonuses ? m.bonuses.reduce((n, b) => n + b.count, 0) : null;
              const top = m.games && m.games.length ? m.games[0] : null;
              const gamesNote = m.gamesCoverage === 'partial'
                ? 'Oyun kayıtları bu ay için kısmi — sayfa sınırı bu ayın içinde doldu'
                : undefined;
              const gamesMissing = m.gamesCoverage === 'none'
                ? 'Bu ay oyun kayıtları okunmadı — sayfa sınırı daha yeni aylarda doldu'
                : undefined;
              return (
                <div key={m.key} style={{ borderBottom: '1px solid var(--border)' }}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    data-caa-clickcard
                    onClick={() => toggle(m.key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(m.key); } }}
                    style={{ ...listRow, gridTemplateColumns: monthCols, borderBottom: 'none', cursor: 'pointer', background: isOpen ? 'var(--surface2)' : 'transparent', border: '1px solid transparent', opacity: m.active ? 1 : 0.6 }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{monthLabel(m.key, lang)}</span>
                      {!m.active && <span style={{ fontSize: '11px', color: 'var(--faint)' }}>Hareket yok</span>}
                    </span>
                    <SumCell value={dep ? String(dep.count) : ''} unread={!dep} strong />
                    <SumCell value={dep && dep.count ? money(dep.total) : dep ? '0' : ''} unread={!dep} strong color="#37c46b" />
                    <SumCell value={dep && dep.count ? money(dep.total / dep.count) : dep ? '0' : ''} unread={!dep} />
                    <SumCell value={dep ? String(dep.byDay.length) : ''} unread={!dep} />
                    <SumCell value={bonusCount != null ? String(bonusCount) : ''} unread={bonusCount == null} />
                    <span style={{ ...ellipsis, fontSize: '12px', color: top ? 'var(--dim)' : 'var(--faint)' }} title={top ? top.name + ' · ' + top.rounds : undefined}>
                      {top ? top.name : m.gamesCoverage === 'none' || !m.games ? '—' : ''}
                    </span>
                    <Icon name={isOpen ? 'expand_less' : 'expand_more'} size={20} color="var(--faint)" />
                  </div>
                  {isOpen && (
                    <div style={{ padding: '16px 18px 2px', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <Icon name="calendar_month" size={18} color="var(--accent)" />
                        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>{monthLabel(m.key, lang)}</span>
                        <span style={{ fontSize: '12px', color: 'var(--faint)' }}>{m.start + ' – ' + m.end}</span>
                      </div>
                      <PeriodBody
                        scope="month"
                        deposits={m.deposits}
                        bonuses={m.bonuses}
                        games={m.games}
                        gamesDistinct={m.gamesDistinct}
                        gamesNote={gamesNote}
                        gamesMissing={gamesMissing}
                        noNameKey={noNameKey}
                        src={src}
                        tz={tz}
                        lang={lang}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Winback({ brands, lang, standalone = false }: { brands: BrandId[]; lang: Lang; standalone?: boolean }) {
  // Marka: üst filtre ∩ BackOffice'li markalar; boş kesişimde tüm BackOffice'li markalar.
  const brandOptions = useMemo(() => {
    const inScope = WINBACK_BRANDS.filter((b) => brands.includes(b));
    return inScope.length ? inScope : WINBACK_BRANDS;
  }, [brands]);
  const [brand, setBrand] = useState<BrandId>(brandOptions[0]);
  useEffect(() => {
    if (!brandOptions.includes(brand)) setBrand(brandOptions[0]);
  }, [brandOptions, brand]);
  const tz = tzOfBrands([brand]);

  const [customerId, setCustomerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<WinbackReport | null>(null);

  const idOk = /^\d{1,12}$/.test(customerId.trim());

  const run = async () => {
    if (!idOk || busy) return;
    setBusy(true);
    setError('');
    try {
      // Sahip 2026-10-07: dönem seçici yok — her zaman tüm zamanlar + altında aylık kırılım.
      const d = await getWinback(brand, customerId.trim(), 'all');
      setData(d);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'request_failed';
      setError(ERROR_NOTE[msg] || 'Analiz alınamadı — ' + msg);
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const src: SrcOf = (key) => data?.sources.find((s) => s.key === key);
  const profileSrc = src('profile');
  const notFound = profileSrc?.status === 'not_found';
  const problems = (data?.sources || []).filter((s) => s.status !== 'ok' && s.status !== 'not_found');
  const noNameKey = Boolean(data?.gamesMeta && data.gamesMeta.rows > 0 && !data.gamesMeta.nameKey);

  return (
    <>
      {/* sorgu */}
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        {brandOptions.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' }}>MARKA</span>
            <Select value={brand} onChange={(v) => setBrand(v as BrandId)} options={brandOptions.map((b) => ({ value: b, label: brandLabel(b) }))} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '0 1 260px' }}>
          <span style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' }}>MÜŞTERİ ID</span>
          <input
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
            placeholder="BackOffice müşteri ID"
            inputMode="numeric"
            autoFocus
            style={{ ...inputStyle, padding: '9px 12px', fontSize: '13px' }}
          />
        </div>
        <button onClick={() => void run()} disabled={!idOk || busy} style={{ ...primaryBtnStyle, padding: '10px 18px', opacity: !idOk || busy ? 0.55 : 1, cursor: !idOk || busy ? 'default' : 'pointer' }}>
          {busy ? 'Analiz ediliyor…' : 'Analiz Et'}
        </button>
        {busy && <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Tüm zamanlar taraması biraz sürebilir</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '14px' }}>
          {brandOptions.length === 1 && <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{brandLabel(brand)}</span>}
          {!standalone && (
            <a href="/winback" target="_blank" rel="noopener" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
              <Icon name="open_in_new" size={16} color="var(--accent)" />
              <span>Ayrı sekmede aç</span>
            </a>
          )}
        </span>
      </div>

      {error && (
        <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#ef5d5d' }}>
          <Icon name="error" size={18} color="#ef5d5d" />
          <span>{error}</span>
        </div>
      )}

      {busy && !data && <Skeleton heights={[120, 200, 200]} />}

      {!busy && !data && !error && (
        <EmptyState icon="person_search" title="Müşteri analizi" reason="BackOffice müşteri ID'sini yazıp Analiz Et'e basın. Kayıt tarihi, son giriş, son 10 yatırım / çekim / oyun işlemi / kupon, tüm zamanlar için yatırım / bonus / oyun kırılımı ve altında her ayın ayrı kırılımı gelir." />
      )}

      {data && notFound && (
        <EmptyState icon="person_off" title="Müşteri bulunamadı" reason={brandLabel(data.brand) + ' BackOffice’inde ' + data.customerId + ' numaralı müşteri yok.'} />
      )}

      {data && !notFound && (
        <div style={{ opacity: busy ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* okunamayan kaynaklar */}
          {problems.length > 0 && (
            <div style={{ ...cardStyle, padding: '10px 16px', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#e0a23c' }}>
              {problems.map((s) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="warning" size={16} color="#e0a23c" />
                  <span>{SOURCE_LABEL[s.key]}</span>
                  <span style={{ color: 'var(--faint)' }}>·</span>
                  <span>{STATUS_NOTE[s.status as Exclude<WinbackSourceStatus, 'ok'>]}</span>
                  {s.status === 'error' && s.message && <span style={{ color: 'var(--faint)', fontSize: '11px' }} title={s.message}>{'— ' + s.message.slice(0, 90)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* profil */}
          <div style={{ ...cardStyle, marginBottom: '18px', overflow: 'hidden' }}>
            <CardHead
              icon="badge"
              title={data.profile ? (data.profile.username || '#' + data.customerId) : '#' + data.customerId}
              sub={data.profile ? [data.profile.name, data.profile.group, brandLabel(data.brand)].filter(Boolean).join(' · ') : brandLabel(data.brand)}
              right={<span style={{ fontSize: '11.5px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{'ID ' + data.customerId}</span>}
            />
            {data.profile ? (
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', padding: '14px 18px' }}>
                <KpiTile small label="KAYIT TARİHİ" value={data.profile.registerDate ? fmtStamp(data.profile.registerDate, tz, true) : ''} />
                <KpiTile small label="SON GİRİŞ" value={data.profile.lastLogin ? fmtStamp(data.profile.lastLogin, tz, true) : ''} />
                <KpiTile small label="BAKİYE" value={money(data.profile.balance)} />
                <KpiTile small label="TOPLAM YATIRIM" value={money(data.profile.totalDeposit)} color="#37c46b" />
                <KpiTile small label="TOPLAM ÇEKİM" value={money(data.profile.totalWithdraw)} />
              </div>
            ) : (
              <Unavailable status={profileSrc?.status || 'error'} message={profileSrc?.message} />
            )}
          </div>

          {/* son 10: yatırım | çekim / oyun işlemi | kupon — döneme bağlı DEĞİL */}
          <div style={cardGrid}>
            <LastTenCard icon="history" title="Son 10 yatırım" tz={tz} scannedFrom={data.lastDepositsScannedFrom} src={src('lastDeposits')} rows={data.lastDeposits} emptyText="Taranan dönemde yatırım yok">
              <PaymentRows rows={data.lastDeposits || []} tz={tz} color="#37c46b" />
            </LastTenCard>
            <LastTenCard icon="payments" title="Son 10 çekim" tz={tz} scannedFrom={data.lastWithdrawalsScannedFrom} src={src('lastWithdrawals')} rows={data.lastWithdrawals} emptyText="Taranan dönemde çekim yok">
              <PaymentRows rows={data.lastWithdrawals || []} tz={tz} color="#ef5d5d" />
            </LastTenCard>
            <LastTenCard
              icon="stadia_controller"
              title="Son 10 oyun işlemi"
              tz={tz}
              scannedFrom={data.lastGamesMeta?.scannedFrom}
              src={src('lastGames')}
              rows={data.lastGames}
              emptyText="Taranan dönemde oyun işlemi yok"
              note={data.lastGamesMeta?.truncated ? 'Oyun işlemleri sayfa sınırına ulaştı — liste eksik olabilir' : undefined}
            >
              <GameTxRows rows={data.lastGames || []} tz={tz} />
            </LastTenCard>
            <LastTenCard
              icon="sports_soccer"
              title="Son 10 kupon"
              tz={tz}
              scannedFrom={data.lastCouponsMeta?.scannedFrom}
              src={src('lastCoupons')}
              rows={data.lastCoupons}
              emptyText="Taranan dönemde kupon yok"
              note={[
                data.lastCouponsMeta?.truncated ? 'Kupon listesi sayfa sınırına ulaştı — daha eski kuponlar eksik olabilir' : '',
                data.lastCouponsMeta?.failedProviders?.length ? 'Okunamayan sağlayıcı: ' + data.lastCouponsMeta.failedProviders.join(', ') : '',
              ].filter(Boolean).join(' · ') || undefined}
            >
              <CouponRows rows={data.lastCoupons || []} tz={tz} />
            </LastTenCard>
          </div>

          {/* tüm zamanlar başlığı */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '6px 0 12px', flexWrap: 'wrap' }}>
            <Icon name={data.month.allTime ? 'all_inclusive' : 'calendar_month'} size={20} color="var(--accent)" />
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>{data.month.allTime ? 'Tüm zamanlar' : monthLabel(data.month.key, lang)}</span>
            {data.month.start && <span style={{ fontSize: '12px', color: 'var(--faint)' }}>{data.month.start + ' – ' + data.month.end}</span>}
            {data.month.allTime && data.month.capped && data.month.maxMonths && (
              <span style={{ fontSize: '11.5px', color: '#e0a23c' }}>{'Son ' + data.month.maxMonths + ' ay ile sınırlı'}</span>
            )}
          </div>
          <PeriodBody
            scope={data.month.allTime ? 'all' : 'month'}
            deposits={data.deposits}
            bonuses={data.bonuses}
            games={data.games}
            gamesDistinct={data.gamesMeta ? data.gamesMeta.distinct : null}
            gamesNote={data.gamesMeta?.truncated
              ? data.gamesMeta.coveredFrom
                ? 'Oyun kayıtları ' + data.gamesMeta.rows + ' satırda kesildi — sıralama ' + fmtStamp(data.gamesMeta.coveredFrom, tz, true) + ' sonrasını kapsıyor'
                : 'Oyun kayıtları ' + data.gamesMeta.rows + ' satırda kesildi — sıralama yalnız okunan kısmı yansıtır'
              : undefined}
            noNameKey={noNameKey}
            src={src}
            tz={tz}
            lang={lang}
          />

          {/* aylık kırılım — tüm zamanların altında, ayrı filtre yok */}
          {data.monthly && (
            <MonthlySection
              key={data.brand + '|' + data.customerId}
              months={data.monthly}
              noNameKey={noNameKey}
              src={src}
              tz={tz}
              lang={lang}
            />
          )}
        </div>
      )}
    </>
  );
}
